var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { msg, localized } from '@lit/localize';
import '../icon/Icon.js';
const ICON_PX_BY_SIZE = {
    small: 14,
    medium: 18,
    large: 24,
};
let IconButton = class IconButton extends LitElement {
    constructor() {
        super(...arguments);
        this.size = 'medium';
        this.icon = '';
        this.ariaLabel = '';
        this.variant = 'primary';
        this.special = false;
        this.outlined = false;
        this.dashed = false;
        this.disabled = false;
    }
    handleClick(e) {
        this.dispatchEvent(new CustomEvent('button-click', { detail: { ariaLabel: this.ariaLabel } }));
        e.preventDefault();
        e.stopPropagation();
    }
    render() {
        const variantClass = `ggo-button--${this.variant}`;
        const sizeClass = `ggo-button--${this.size}`;
        const specialClass = this.special ? 'ggo-button--special' : '';
        const outlinedClass = this.outlined ? 'ggo-button--outlined' : '';
        const dashedClass = this.dashed ? 'ggo-button--dashed' : '';
        return html `
      <button
        class="ggo-button ${variantClass} ${sizeClass} ${specialClass} ${outlinedClass} ${dashedClass}"
        @click=${this.handleClick}
        ?disabled=${this.disabled}
        aria-label=${this.ariaLabel || msg('icon button', { id: 'iconButton.ariaLabel', desc: 'Default accessible label for an icon button' })}
      >
        <span class="ggo-button__icon">
          ${this.iconName
            ? html `<basic-icon .name=${this.iconName} .size=${ICON_PX_BY_SIZE[this.size]}></basic-icon>`
            : this.icon
                ? unsafeHTML(this.icon)
                : html `<slot></slot>`}
        </span>
      </button>
    `;
    }
};
IconButton.styles = css `
    :host {
      font-family: inherit;
    }

    .ggo-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      background: #ffffff;
      border: 1px solid #ccc;
      cursor: pointer;
      transition: background 0.3s, border-color 0.3s, transform 0.2s;
      border-radius: 8px;
      box-sizing: border-box;
      flex-shrink: 0;
    }

    .ggo-button:hover {
      background: #f0f0f0;
      border-color: #aaa;
    }

    .ggo-button:active {
      transform: scale(0.95);
    }

    /* Variants */
    .ggo-button--primary {
      background: #0078d4;
      color: #fff;
      border: none;
    }
    .ggo-button--primary:hover {
      background: #005a9e;
    }

    .ggo-button--secondary {
      background: #f0f0f0;
      color: #333;
    }
    .ggo-button--secondary:hover {
      background: #e0e0e0;
    }

    .ggo-button--success {
      background: #28a745;
      color: white;
    }
    .ggo-button--success:hover {
      background: #218838;
    }

    .ggo-button--danger {
      background: #dc3545;
      color: white;
    }
    .ggo-button--danger:hover {
      background: #c82333;
    }

    .ggo-button--warning {
      background: #ffc107;
      color: black;
    }
    .ggo-button--warning:hover {
      background: #e0a800;
    }

    /* Special (gradient) for each variant */
    .ggo-button--primary.ggo-button--special {
      background: linear-gradient(135deg, #0078d4, #00aaff);
    }
    .ggo-button--primary.ggo-button--special:hover {
      background: linear-gradient(135deg, #005a9e, #0088cc);
    }

    .ggo-button--secondary.ggo-button--special {
      background: linear-gradient(135deg, #cccccc, #f0f0f0);
    }
    .ggo-button--secondary.ggo-button--special:hover {
      background: linear-gradient(135deg, #aaaaaa, #e0e0e0);
    }

    .ggo-button--success.ggo-button--special {
      background: linear-gradient(135deg, #28a745, #5cd65c);
    }
    .ggo-button--success.ggo-button--special:hover {
      background: linear-gradient(135deg, #1e7e34, #4caf50);
    }

    .ggo-button--danger.ggo-button--special {
      background: linear-gradient(135deg, #dc3545, #ff6f71);
    }
    .ggo-button--danger.ggo-button--special:hover {
      background: linear-gradient(135deg, #b52a37, #e63946);
    }

    .ggo-button--warning.ggo-button--special {
      background: linear-gradient(135deg, #ffc107, #ffe066);
    }
    .ggo-button--warning.ggo-button--special:hover {
      background: linear-gradient(135deg, #e0a800, #ffd54f);
    }

    /* Outlined (ghost) — neutral white at rest, border + text shift to
       the variant colour on hover. Backgrounds use the variant colour at
       a low alpha so the icon stays readable. */
    .ggo-button--outlined {
      background: #ffffff;
      border: 1px solid #e0e6ef;
      color: #6b7280;
    }
    .ggo-button--outlined:hover {
      background: #f5faff;
    }
    .ggo-button--primary.ggo-button--outlined:hover {
      background: #f5faff;
      border-color: #0078d4;
      color: #0078d4;
    }
    .ggo-button--secondary.ggo-button--outlined:hover {
      background: #f4f5f7;
      border-color: #6c757d;
      color: #4d5358;
    }
    .ggo-button--success.ggo-button--outlined:hover {
      background: #effaf2;
      border-color: #28a745;
      color: #218838;
    }
    .ggo-button--danger.ggo-button--outlined:hover {
      background: #fdecea;
      border-color: #c2574f;
      color: #c2574f;
    }
    .ggo-button--warning.ggo-button--outlined:hover {
      background: #fff8e1;
      border-color: #e0a800;
      color: #8a6500;
    }
    /* Outlined wins over the filled variant + special gradient — both are
       authored above this rule, so the cascade picks .outlined hover here. */

    /* Dashed — transparent fill, dashed border in the variant colour.
       Mirrors <simple-button> dashed styles. */
    .ggo-button--primary.ggo-button--dashed {
      background: transparent;
      color: #0078d4;
      border: 2px dashed #0078d4;
    }
    .ggo-button--primary.ggo-button--dashed:hover {
      background: rgba(0, 120, 212, 0.08);
      border-color: #005a9e;
      color: #005a9e;
    }
    .ggo-button--secondary.ggo-button--dashed {
      background: transparent;
      color: #6c757d;
      border: 2px dashed #6c757d;
    }
    .ggo-button--secondary.ggo-button--dashed:hover {
      background: rgba(108, 117, 125, 0.08);
      border-color: #4d5358;
      color: #4d5358;
    }
    .ggo-button--success.ggo-button--dashed {
      background: transparent;
      color: #28a745;
      border: 2px dashed #28a745;
    }
    .ggo-button--success.ggo-button--dashed:hover {
      background: rgba(40, 167, 69, 0.08);
      border-color: #218838;
      color: #218838;
    }
    .ggo-button--danger.ggo-button--dashed {
      background: transparent;
      color: #dc3545;
      border: 2px dashed #dc3545;
    }
    .ggo-button--danger.ggo-button--dashed:hover {
      background: rgba(220, 53, 69, 0.08);
      border-color: #b52a37;
      color: #b52a37;
    }
    .ggo-button--warning.ggo-button--dashed {
      background: transparent;
      color: #b78400;
      border: 2px dashed #ffc107;
    }
    .ggo-button--warning.ggo-button--dashed:hover {
      background: rgba(255, 193, 7, 0.12);
      border-color: #e0a800;
      color: #8a6500;
    }

    /* Sizes — always square */
    .ggo-button--small {
      width: 28px;
      height: 28px;
    }
    .ggo-button--small .ggo-button__icon {
      width: 14px;
      height: 14px;
    }

    .ggo-button--medium {
      width: 34px;
      height: 34px;
    }
    .ggo-button--medium .ggo-button__icon {
      width: 18px;
      height: 18px;
    }

    .ggo-button--large {
      width: 47px;
      height: 47px;
    }
    .ggo-button--large .ggo-button__icon {
      width: 24px;
      height: 24px;
    }

    .ggo-button__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
    }

    .ggo-button__icon svg {
      width: 100%;
      height: 100%;
      display: block;
      fill: currentColor;
    }

    /* Disabled state */
    .ggo-button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background: #e0e0e0;
      color: #888;
      border-color: #ccc;
    }

    .ggo-button:disabled:hover {
      background: #e0e0e0;
      border-color: #ccc;
    }
  `;
__decorate([
    property({ type: String })
], IconButton.prototype, "size", void 0);
__decorate([
    property({ type: String, attribute: 'icon-name' })
], IconButton.prototype, "iconName", void 0);
__decorate([
    property({ type: String })
], IconButton.prototype, "icon", void 0);
__decorate([
    property({ type: String, attribute: 'aria-label' })
], IconButton.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: String })
], IconButton.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean })
], IconButton.prototype, "special", void 0);
__decorate([
    property({ type: Boolean })
], IconButton.prototype, "outlined", void 0);
__decorate([
    property({ type: Boolean })
], IconButton.prototype, "dashed", void 0);
__decorate([
    property({ type: Boolean })
], IconButton.prototype, "disabled", void 0);
IconButton = __decorate([
    localized(),
    customElement('icon-button')
], IconButton);
export { IconButton };
//# sourceMappingURL=IconButton.js.map